import React from 'react';
import styled from 'styled-components';
import { StylesConfig } from 'react-select';
import CreatableSelect from 'react-select/creatable';
import theme from '../../../theme';
import { SmallPortalTooltip } from '../../Tooltip';
import dayjs from 'dayjs';
import { components, MultiValueRemoveProps, GroupBase } from 'react-select';
import {
  TooltipContent,
  TooltipDetail,
  TooltipTitle,
} from '../Progress/Tooltip';
import { TopicIcon } from '../TopicPill';
import {
  LearnerBadgeTooltip,
  LearnerOption,
} from '../Modals/AssignLearnersToMentorModal';

const SelectWrapper = styled.div`
  position: relative;
  width: 100%;
`;

const Badge = styled.span`
  font-size: 12px;
  background: ${({ theme }) => theme.colors.bgActive};
  color: ${({ theme }) => theme.colors.textActive};
  border-radius: 8px;
  padding: 0 6px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  min-width: 18px;
  justify-content: center;
`;

const OptionHorizontalWrapper = styled.span`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const MetadataWrapper = styled.span`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 0.65;
  justify-content: flex-end;
  flex-wrap: wrap;
`;

const NameText = styled.span`
  flex: 0.35;
`;
const selectCustomStyles: StylesConfig<LearnerOption, true> = {
  menu: (provided) => ({
    ...provided,
    transition: 'all 0.3s ease-in-out',
    backgroundColor: theme.colors.inputBackground,
    borderColor: theme.colors.bgActive,
    borderStyle: 'solid',
    borderWidth: '1px',
    borderRadius: '8px',
  }),
  menuPortal: (provided) => ({
    ...provided,
    zIndex: 3,
  }),
  container: (provided) => ({
    ...provided,
    margin: 0,
    marginTop: 0,
    paddingBottom: 0,
    width: '100%',
  }),
  valueContainer: (provided) => ({
    ...provided,
    padding: 0,
    flexWrap: 'nowrap',
    overflow: 'auto',
    width: '100%',
    scrollbarWidth: 'none',
  }),
  dropdownIndicator: (provided) => ({ ...provided, display: 'none' }),
  clearIndicator: (provided) => ({ ...provided, display: 'none' }),
  indicatorsContainer: (provided) => ({
    ...provided,
    display: 'none',
  }),
  multiValue: (provided) => ({
    ...provided,
    backgroundColor: theme.colors.bgActive,
    borderRadius: '200px',
    padding: '4px 8px',
    gap: '6px',
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    width: 'max-content',
    minWidth: 'unset',
    transition: 'all 0.3s ease-in-out',
    border: `1px solid ${theme.colors.bgActive}`,
  }),
  multiValueLabel: (provided) => ({
    ...provided,
    textOverflow: 'unset',
    width: '100%',
    fontStyle: 'normal',
    fontWeight: 400,
    fontSize: '13px',
    lineHeight: '18px',
    color: theme.colors.textActive,
    textAlign: 'center',
    padding: 0,
    margin: 0,
  }),
  multiValueRemove: (provided) => ({
    ...provided,
    padding: 2,
    margin: 0,
    transition: 'all 0.3s ease-in-out',
    '&:hover': {
      backgroundColor: theme.colors.elementBgDanger,
      color: theme.colors.textActive,
    },
  }),
  control: (provided) => ({
    ...provided,
    transition: 'all 0.3s ease-in-out',
    backgroundColor: 'transparent',
    borderColor: 'transparent',
    padding: 0,
    margin: 0,
    height: '100%',
    borderRadius: '8px',
    boxShadow: '0 !important',
    outline: 'none',
    '&:hover': {
      borderColor: 'transparent',
    },
    cursor: 'pointer',
  }),
  option: (
    provided,
    { isSelected, isFocused }: { isSelected: boolean; isFocused: boolean }
  ) => ({
    ...provided,
    backgroundColor:
      isSelected || isFocused ? theme.colors.fieldBackground : 'transparent',
    color:
      isSelected || isFocused
        ? theme.colors.textPrimary
        : theme.colors.textRegular,
    cursor: isSelected ? 'not-allowed' : 'default',
    '&:active': {
      color: theme.colors.textPrimary,
      backgroundColor: theme.colors.fieldBackground,
    },
  }),
  singleValue: (provided) => ({
    ...provided,
    color: theme.colors.inputTextActive,
  }),
  input: (provided) => ({
    ...provided,
    color: theme.colors.inputTextActive,
  }),
  placeholder: (provided) => ({
    ...provided,
    color: theme.colors.inputTextPlaceholder,
  }),
};

const MultiValueRemove = (
  props: MultiValueRemoveProps<LearnerOption, true, GroupBase<LearnerOption>>
) => {
  return (
    <components.MultiValueRemove
      {...props}
      innerProps={{
        onPointerDown: (e: React.PointerEvent) => e.stopPropagation(),
        ...props.innerProps,
      }}
    />
  );
};

const formatOptionLabel = (
  option: LearnerOption,
  { context }: { context: 'menu' | 'value' },
  officeTime?: Date
) => {
  if (context === 'menu' && officeTime) {
    // Check if learner has any onboarding choices
    const hasOnboardingChoices =
      option.officeHourPreferences && option.officeHourPreferences.length > 0;

    let iconColor = theme.colors.textRegular;
    let iconClass = '';
    let iconTitle = '';

    if (!hasOnboardingChoices) {
      iconClass = 'fa-solid fa-minus';
      iconColor = theme.colors.textRegular;
      iconTitle = 'No onboarding choices';
    } else {
      // Check if learner's preferences match the office time
      const hasMatchingPreference = option.officeHourPreferences.some(
        (preferenceTime) => {
          return dayjs.utc(preferenceTime).isSame(dayjs.utc(officeTime));
        }
      );
      if (hasMatchingPreference) {
        iconClass = 'fa-solid fa-check';
        iconColor = theme.colors.textSuccess;
        iconTitle = 'Office hour matches onboarding choices';
      } else {
        iconClass = 'fa-solid fa-xmark';
        iconColor = theme.colors.textRegular;
        iconTitle = "Onboarding choices don't match office hour";
      }
    }

    const progressTooltipId = `progress-tooltip-${
      option.sessionId
    }-${officeTime?.toString()}`;

    const roleTooltipId = `role-tooltip-${
      option.sessionId
    }-${officeTime?.toString()}`;

    const matchIconTooltipId = `match-icon-tooltip-${
      option.sessionId
    }-${officeTime?.toString()}`;

    const prefsTooltipId = `prefs-tooltip-${
      option.sessionId
    }-${officeTime?.toString()}`;

    const skillTooltipId = `skill-badges-tooltip-${
      option.sessionId
    }-${officeTime?.toString()}`;

    return (
      <OptionHorizontalWrapper
        key={`${option.sessionId}-option-horizontal-wrapper`}
      >
        <NameText>{option.label}</NameText>
        <MetadataWrapper>
          {option.skillRatings.map((skill) => (
            <>
              <Badge key={skill.iconUrl} data-tooltip-id={skillTooltipId}>
                <TopicIcon src={skill.iconUrl} />
                {skill.rating}/10
              </Badge>
              <SmallPortalTooltip
                anchorSelect={`[data-tooltip-id="${skillTooltipId}"]`}
              >
                <TooltipContent>
                  <TooltipTitle>Skill</TooltipTitle>
                  <TooltipDetail>{skill.topic}</TooltipDetail>
                </TooltipContent>
              </SmallPortalTooltip>
            </>
          ))}

          <Badge data-tooltip-id={roleTooltipId}>{option.role}</Badge>
          <SmallPortalTooltip
            anchorSelect={`[data-tooltip-id="${roleTooltipId}"]`}
          >
            <TooltipContent>
              <TooltipTitle>Role</TooltipTitle>
              <TooltipDetail>{option.role}</TooltipDetail>
            </TooltipContent>
          </SmallPortalTooltip>
          {hasOnboardingChoices && (
            <>
              <Badge data-tooltip-id={prefsTooltipId}>
                #{option.officeHourPreferences.length}
              </Badge>
              <SmallPortalTooltip
                anchorSelect={`[data-tooltip-id="${prefsTooltipId}"]`}
              >
                <TooltipContent>
                  <TooltipTitle>Office hour preferences</TooltipTitle>
                  {option.officeHourPreferences.map((time, idx) => {
                    const local = dayjs.utc(time).local();
                    let offset = local.format('Z');
                    if (offset.endsWith(':00'))
                      offset = offset.replace(':00', '');
                    return (
                      <TooltipDetail key={idx}>
                        {local.format('dddd[s] hA')}
                      </TooltipDetail>
                    );
                  })}
                </TooltipContent>
              </SmallPortalTooltip>
            </>
          )}
          <Badge data-tooltip-id={progressTooltipId}>
            {typeof option.progress === 'number'
              ? `${option.progress}%`
              : 'N/A'}
          </Badge>
          <SmallPortalTooltip
            anchorSelect={`[data-tooltip-id="${progressTooltipId}"]`}
          >
            <TooltipContent>
              <TooltipTitle>Progress</TooltipTitle>
              <TooltipDetail>
                {typeof option.progress === 'number'
                  ? `${option.progress}%`
                  : 'N/A'}
              </TooltipDetail>
            </TooltipContent>
          </SmallPortalTooltip>
          <span
            data-tooltip-id={matchIconTooltipId}
            style={{ display: 'flex', alignItems: 'center' }}
          >
            <i className={iconClass} style={{ color: iconColor }} />
          </span>
          <SmallPortalTooltip
            anchorSelect={`[data-tooltip-id="${matchIconTooltipId}"]`}
          >
            <TooltipContent>
              <TooltipDetail>{iconTitle}</TooltipDetail>
            </TooltipContent>
          </SmallPortalTooltip>
        </MetadataWrapper>
      </OptionHorizontalWrapper>
    );
  }

  if (context === 'value') {
    const tooltipId = `learner-dropdown-${option.sessionId}`;
    return <span data-tooltip-id={tooltipId}>{option.label}</span>;
  }

  return option.label;
};

export default function LearnerAssignmentCell({
  combinationId,
  assignedLearners,
  availableLearners,
  onChange,
  officeTime,
}: {
  combinationId: string;
  assignedLearners: LearnerOption[];
  availableLearners: LearnerOption[];
  onChange: (combinationId: string, newAssignments: LearnerOption[]) => void;
  officeTime: Date;
}) {
  const onValuesChange = (newValue: readonly LearnerOption[]) => {
    onChange(combinationId, [...newValue]);
  };

  const customFormatOptionLabel = (
    option: LearnerOption,
    context: { context: 'menu' | 'value' }
  ) => {
    return formatOptionLabel(option, context, officeTime);
  };

  return (
    <React.Fragment key={`${combinationId}-select-fragment`}>
      <SelectWrapper>
        <CreatableSelect
          key={`${combinationId}-select`}
          options={availableLearners}
          value={assignedLearners}
          isMulti
          menuPortalTarget={document.body}
          placeholder="Assign learners..."
          isValidNewOption={() => false}
          styles={selectCustomStyles}
          formatOptionLabel={customFormatOptionLabel}
          onChange={onValuesChange}
          components={{
            MultiValueRemove,
          }}
          getOptionValue={(option) => option.sessionId.toString()}
        />
      </SelectWrapper>

      {assignedLearners.map((learner) => {
        const tooltipId = `learner-dropdown-${learner.sessionId}`;

        return (
          <SmallPortalTooltip
            key={tooltipId}
            anchorSelect={`[data-tooltip-id="${tooltipId}"]`}
          >
            <LearnerBadgeTooltip
              email={learner.email}
              officeHoursPrefs={learner.officeHourPreferences}
              skillRatings={learner.skillRatings?.reduce((acc, s) => {
                acc[s.topic] = s.rating;
                return acc;
              }, {} as Record<string, number>)}
              progress={learner.progress}
              role={learner.role}
            />
          </SmallPortalTooltip>
        );
      })}
    </React.Fragment>
  );
}
